import React, { useState } from "react";
import { Box, Typography, TextField, Button, Alert, Link as MuiLink } from "@mui/material";
import { forgotPassword, resetPassword } from "../api";
import { C } from "../constants/colors";
import AuthLayout from "../layout/AuthLayout";
import PasswordField, { fieldTone } from "../components/PasswordField";
import { formatApiError } from "../utils/apiError";
import { isValidEmail, passwordIsStrong, textFieldToneSx } from "../utils/passwordRules";
import { useAppNotify } from "../components/AppNotify";
import { authButtonSx, authLabelSx } from "../theme/authStyles";

export default function ForgotPassword({ goToLogin }) {
  const { notify } = useAppNotify();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [code, setCode] = useState("");
  const [previewCode, setPreviewCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwTouched, setPwTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const emailOk = isValidEmail(email);
  const pwOk = passwordIsStrong(password);
  const match = password.length > 0 && password === confirm;

  const requestCode = async (e) => {
    e.preventDefault();
    setEmailTouched(true);
    setError("");
    if (!emailOk) {
      notify({ type: "error", message: "Enter a valid email address" });
      return;
    }
    setBusy(true);
    try {
      const res = await forgotPassword({ email: email.trim() });
      setInfo(res.message || "If that email is registered, use the reset code to set a new password.");
      setPreviewCode(res.code || "");
      setStep(2);
      notify({
        type: "success",
        title: "Reset code ready",
        message: res.code ? `Your code is ${res.code}` : (res.message || "Check the next step."),
      });
    } catch (err) {
      const msg = formatApiError(err, "Could not start reset");
      setError(msg);
      notify({ type: "error", message: msg });
    }
    setBusy(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setPwTouched(true);
    setConfirmTouched(true);
    setError("");
    if (!emailOk || !String(code).trim() || !pwOk || !match) {
      setError("Fix the highlighted fields, then try again.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword({ email: email.trim(), code: String(code).trim(), password });
      setInfo("Password updated. You can sign in now.");
      notify({ type: "success", title: "Password updated", message: "You can sign in with your new password." });
      setTimeout(() => goToLogin?.(), 900);
    } catch (err) {
      const msg = formatApiError(err, "Reset failed");
      setError(msg);
      notify({ type: "error", message: msg });
    }
    setBusy(false);
  };

  return (
    <AuthLayout
      title="Reset password"
      subtitle="Enter your OKRion email and we’ll send a reset code."
      footer={
        <MuiLink component="button" type="button" onClick={goToLogin} sx={{ fontWeight: 600, color: C.primary }}>
          Back to sign in
        </MuiLink>
      }
    >

        {info && <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }}>{info}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>{error}</Alert>}

        {step === 1 ? (
          <Box component="form" onSubmit={requestCode} noValidate>
            <Typography sx={authLabelSx}>Email</Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="you@institution.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              autoComplete="email"
              sx={{ ...textFieldToneSx(fieldTone({ value: email, touched: emailTouched, valid: emailOk }), C), mb: 2.5 }}
            />
            <Button type="submit" fullWidth variant="contained" disabled={busy} sx={authButtonSx}>
              {busy ? "Sending…" : "Get reset code"}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={submitReset} noValidate>
            {previewCode ? (
              <Alert severity="info" sx={{ mb: 2, borderRadius: "8px" }}>
                Email sending is not configured on the server. Use this code: <strong>{previewCode}</strong>
              </Alert>
            ) : null}
            <Typography sx={authLabelSx}>6-digit code</Typography>
            <TextField
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              sx={{ ...textFieldToneSx("idle", C), mb: 2 }}
            />
            <PasswordField
              label="New password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setPwTouched(true); }}
              autoComplete="new-password"
              showRules
              tone={fieldTone({ value: password, touched: pwTouched, valid: pwOk })}
            />
            <PasswordField
              label="Confirm new password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setConfirmTouched(true); }}
              autoComplete="new-password"
              tone={fieldTone({ value: confirm, touched: confirmTouched, valid: match && pwOk, required: true })}
              helperText={
                !confirmTouched && !confirm ? "" : match && pwOk ? "Passwords match" : "Passwords do not match"
              }
            />
            <Button type="submit" fullWidth variant="contained" disabled={busy} sx={authButtonSx}>
              {busy ? "Updating…" : "Save new password"}
            </Button>
          </Box>
        )}
    </AuthLayout>
  );
}
